import { Segmented } from 'antd'
import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router-dom'

import {
  AuditExportsPanel,
  AuditPanelHeader
} from 'src/features/workspaces/components'
import { useHasWorkspacePermissions } from 'src/hooks'
import { WorkspacePermissionCode } from 'src/utils'

import { EventsPanel } from './EventsPanel'

const TAB_KEYS = ['events', 'exports'] as const
// `section` pins the app-detail page to the Activity nav item — dropping it
// falls the page back to Overview. The panel-scoped list params (page, search,
// ordering, …) are intentionally *not* kept: each view starts clean.
const KEEP_QUERY_KEYS = ['section', 'tab', 'audit_tab'] as const

type TabKey = (typeof TAB_KEYS)[number]

type Props = {
  applicationId: number
  organizationId: number
}

const isTabKey = (value: string | null): value is TabKey =>
  TAB_KEYS.includes(value as TabKey)

export const AppDetailAuditTab = ({ applicationId, organizationId }: Props) => {
  const { t } = useTranslation()
  const [searchParams, setSearchParams] = useSearchParams()

  // Exports carry their own code, so a member with only `audit.view` gets
  // neither the tab nor a way onto it through a stale `?audit_tab=exports`.
  const mayExportAudit = useHasWorkspacePermissions(
    WorkspacePermissionCode.AUDIT_EXPORT
  )

  const rawTab = searchParams.get('audit_tab')
  const tab: TabKey =
    isTabKey(rawTab) && (rawTab !== 'exports' || mayExportAudit)
      ? rawTab
      : 'events'

  const handleTabChange = (nextTab: TabKey) => {
    const next = new URLSearchParams()

    for (const key of KEEP_QUERY_KEYS) {
      for (const value of searchParams.getAll(key)) {
        next.append(key, value)
      }
    }

    if (nextTab === 'events') {
      next.delete('audit_tab')
    } else {
      next.set('audit_tab', nextTab)
    }

    setSearchParams(next)
  }

  const viewSelector = mayExportAudit ? (
    <Segmented<TabKey>
      value={tab}
      onChange={handleTabChange}
      options={[
        { value: 'events', label: t('workspace.apps.auditTab.tabs.events') },
        { value: 'exports', label: t('workspace.apps.auditTab.tabs.exports') }
      ]}
    />
  ) : null

  const header = (
    <AuditPanelHeader
      title={t('workspace.apps.auditTab.title')}
      subtitle={t('workspace.apps.auditTab.subtitle')}
    />
  )

  return (
    <>
      {tab === 'events' && (
        <EventsPanel
          applicationId={applicationId}
          organizationId={organizationId}
          viewSelector={viewSelector}
          header={header}
        />
      )}
      {tab === 'exports' && mayExportAudit && (
        <AuditExportsPanel
          workspaceId={organizationId}
          idValid={organizationId > 0}
          applicationId={applicationId}
          viewSelector={viewSelector}
          header={header}
        />
      )}
    </>
  )
}
